import { get, toString } from 'lodash';
import {
  type IThemeRes,
  ApiResponse,
  CCApiResponse,
  IAssembly,
  IAssemblyComponent,
  IAssemblyListing,
  IComponent,
  IComponentType,
  ILayout,
  ILayoutType,
} from 'src/interfaces';
import { uid } from 'uid';

export const mapAllAssemblies = (
  response: CCApiResponse,
  layoutTypes: ILayoutType[],
  assembliesWithChildren?: Array<{
    id: number;
    childrenAssemblies?: IAssembly[];
  }>,
): IAssembly[] => {
  return (
    response?.data.map((assembly: ApiResponse) => {
      const layouts: ILayout[] = get(response, 'includes.layouts.data', []);
      const layout = layouts.find((item) => item.id === assembly.layout_id);

      const childAssembly = assembliesWithChildren?.find(
        (item) => item.id === assembly.id,
      );

      if (layout) {
        layout.layoutType = layoutTypes.find(
          (item) => item.id === layout.layout_type_id,
        );
      }

      return mapAssembly({
        assembly,
        layout: layout,
        childrenAssemblies: childAssembly?.childrenAssemblies,
      });
    }) ?? []
  );
};

export const mapAssemblyListingData = (
  assembliesData: IAssemblyListing[],
): IAssemblyListing[] => {
  return assembliesData.map((assemblyData) => {
    const layout: ILayout = {
      id: assemblyData.layout_id,
      name: assemblyData.layout_channel_name,
      channel_name: assemblyData.layout_channel_name,
      layout_type_id: 0,
      config: undefined,
      cod_position: undefined,
      status: undefined,
      created_at: '',
      modified_at: '',
      screens: [],
    };

    const childrenAssemblies = assemblyData.childrenAssemblies
      ? mapAssemblyListingData(assemblyData.childrenAssemblies)
      : [];

    const siblingAssemblies = assemblyData.siblingAssemblies
      ? mapAssemblyListingData(assemblyData.siblingAssemblies)
      : [];

    return {
      ...assemblyData,
      layout,
      childrenAssemblies,
      siblingAssemblies,
    };
  });
};

export const mapAssembly = ({
  assembly,
  childrenAssemblies = [],
  siblingAssemblies = [],
  parentAssembly,
  layout,
}: {
  assembly: ApiResponse;
  childrenAssemblies?: IAssembly[];
  siblingAssemblies?: IAssembly[];
  parentAssembly?: IAssembly;
  layout?: ILayout;
}): IAssembly => {
  return {
    id: assembly.id,
    name: assembly.name,
    status: assembly.status,
    created_at: assembly.created_at,
    modified_at: assembly.modified_at,
    layout_id: assembly.layout_id,
    assembly_tag_id: assembly.assembly_tag_id,
    layout: layout,
    variation_parent_id: assembly.variation_parent_id,
    parentAssembly: parentAssembly,
    childrenAssemblies: childrenAssemblies,
    siblingAssemblies: siblingAssemblies,
    config: assembly?.config,
  };
};

export const mapAssemblyComponents = (
  response: CCApiResponse,
  currentAssemblyId?: string,
): {
  data: IAssemblyComponent[];
  parentAssemblyComponents: IAssemblyComponent[];
  backupAssemblyComponents: IAssemblyComponent[];
  parentBackupAssemblyComponents: IAssemblyComponent[];
} => {
  const assemblyComponents = response.data.map(
    (assemblyComponent: ApiResponse) =>
      mapAssemblyComponent(
        assemblyComponent,
        get(response, 'includes.components.data', []),
      ),
  );

  const formattedAssemblyComponents = formatAssemblyComponents(
    assemblyComponents,
    get(response, 'includes.assemblies.data', []),
    currentAssemblyId,
  );

  return formattedAssemblyComponents;
};

export const mapAssemblyComponent = (
  assemblyComponent: ApiResponse,
  components: IComponent[],
) => {
  const component = components
    .map((component) => ({
      ...component,
    }))
    .find((item) => item.id === assemblyComponent.component_id);

  return {
    ...assemblyComponent,
    // TODO: Do we need this uid?
    uid: uid(),
    component: component,
  } as IAssemblyComponent;
};

export const formatAssemblyComponents = (
  assemblyComponents: IAssemblyComponent[],
  assemblies: IAssembly[],
  currentAssemblyId: string = '',
) => {
  const backupAssemblyComponents = assemblyComponents.filter(
    (item) =>
      item.backup_order && item.assembly_id === Number(currentAssemblyId),
  );

  const nonBackupAssemblyComponents = assemblyComponents.filter(
    (item) => !item.backup_order,
  );

  const parentAssemblyComponents = findAssemblyComponentsByAssemblyId(
    nonBackupAssemblyComponents,
    assemblies
      .filter((item) => !item.variation_parent_id)
      .map((item) => item.id),
  );
  const parentBackupAssemblyComponents =
    findBackupAssemblyComponentsByAssemblyId(
      assemblyComponents,
      assemblies
        .filter((item) => !item.variation_parent_id)
        .map((item) => item.id),
    );

  const childAssemblyComponents = findAssemblyComponentsByAssemblyId(
    nonBackupAssemblyComponents,
    assemblies
      .filter((item) => Boolean(item.variation_parent_id))
      .map((item) => item.id),
  );

  return {
    data:
      toString(parentAssemblyComponents[0]?.assembly_id) === currentAssemblyId
        ? parentAssemblyComponents
        : childAssemblyComponents,
    parentAssemblyComponents:
      toString(parentAssemblyComponents[0]?.assembly_id) === currentAssemblyId
        ? []
        : parentAssemblyComponents,
    backupAssemblyComponents,
    parentBackupAssemblyComponents:
      toString(parentBackupAssemblyComponents[0]?.assembly_id) ===
      currentAssemblyId
        ? []
        : parentBackupAssemblyComponents,
  };
};

export const findDeleteAssemblyComponents = (
  originalComponents: IAssemblyComponent[],
  backupAssemblyComponents: IAssemblyComponent[],
  targetComponents: Partial<IAssemblyComponent>[],
) => {
  const originalAssemblyComponent = [
    ...originalComponents,
    ...backupAssemblyComponents,
  ];
  const deleteComponents = originalAssemblyComponent.filter((item) => {
    const found = targetComponents.find(
      (assemblyComponent) => item.id === assemblyComponent.id,
    );

    return !found;
  });

  return deleteComponents;
};

export const findAssemblyComponentsByAssemblyId = (
  assemblyComponents: IAssemblyComponent[],
  assemblyIDs: number[],
) => {
  return assemblyComponents.filter(
    (item) => assemblyIDs.includes(item.assembly_id) && item.backup_order === 0,
  );
};

export const findBackupAssemblyComponentsByAssemblyId = (
  assemblyComponents: IAssemblyComponent[],
  assemblyIDs: number[],
) => {
  return assemblyComponents.filter(
    (item) => assemblyIDs.includes(item.assembly_id) && item.backup_order !== 0,
  );
};

export const mapViewAssemblyComponents = (
  response: CCApiResponse,
  themes: IThemeRes[],
): (IAssembly & {
  parent: IAssembly;
  data: IAssemblyComponent[];
  parentAssemblyComponents: IAssemblyComponent[];
  backupAssemblyComponents: IAssemblyComponent[];
  parentBackupAssemblyComponents: IAssemblyComponent[];
})[] => {
  return get(response, 'data', []).map((assembly: ApiResponse) => {
    const parent = get(assembly, 'parent') as IAssembly;
    const components = get(assembly, 'components', []) as IComponent[];
    const componentTypes = get(
      assembly,
      'componentTypes',
      [],
    ) as IComponentType[];
    const allAssemblyComponents = get(
      assembly,
      'assemblyComponents',
      [],
    ) as IAssemblyComponent[];

    let data: IAssemblyComponent[] = [];
    let parentAssemblyComponents: IAssemblyComponent[] = [];
    let backupAssemblyComponents: IAssemblyComponent[] = [];
    let parentBackupAssemblyComponents: IAssemblyComponent[] = [];

    for (const component of components) {
      component.theme = themes.find((theme) => theme.id === component.theme_id);

      component.component_type = componentTypes.find(
        (item) => item.id === component.component_type_id,
      );
    }

    for (const item of allAssemblyComponents) {
      item.uid = uid();

      item.component = components.find(
        (component) => component.id === item.component_id,
      );

      if (item.backup_order) {
        if (parent && item.assembly_id === parent.id) {
          parentBackupAssemblyComponents.push(item);
        } else {
          backupAssemblyComponents.push(item);
        }
      } else {
        if (parent && item.assembly_id === parent.id) {
          parentAssemblyComponents.push(item);
        } else {
          data.push(item);
        }
      }
    }

    return {
      ...assembly,
      data,
      parentAssemblyComponents,
      backupAssemblyComponents,
      parentBackupAssemblyComponents,
    };
  });
};
